import { useState } from "react";
import { Icon } from "@/components/ui/Icon";
import { ApiError } from "@/lib/api";
import { showToast } from "@/lib/toast";
import { formatMemoDate } from "@/lib/format";
import { t } from "@/lib/strings";
import type { MemoMutations } from "@/hooks/useMemo";
import type { Memo } from "@/lib/types";

interface Props {
  memo: Memo;
  mut: MemoMutations;
}

/** Click-to-edit meeting date, rendered as the Protokoll title. Gated on the SAME
 *  right the backend enforces on PATCH /memos/{id}: finalized → can_edit_finalized,
 *  draft → can_edit_draft. Without the right it is display-only (no affordance).
 *
 *  🔴 Reuses the safe date-input pattern (the Frist editor in MemoEntryRow, the
 *  log-date divider in MemoBlockView): a native date input fires onChange per SEGMENT,
 *  so typing (which briefly forms a valid date like 0002-09-30) and the calendar's
 *  month arrows must NOT commit. Commit only on Enter or the ✓; Esc discards; there is
 *  deliberately NO onBlur — a blur fires the instant you reach for the ✓. */
export function MemoDateEditor({ memo, mut }: Props) {
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState("");

  const canEdit =
    memo.status === "finalized" ? memo.access.can_edit_finalized : memo.access.can_edit_draft;
  const label = formatMemoDate(memo.meeting_date);

  const iconBtn = {
    display: "inline-flex",
    border: "1px solid var(--hairline)",
    borderRadius: 8,
    background: "transparent",
    padding: 5,
    cursor: "pointer",
    lineHeight: 0,
  } as const;

  if (!editing) {
    if (!canEdit) return <>{label}</>;
    return (
      <span style={{ display: "inline-flex", alignItems: "baseline", gap: 8 }}>
        {label}
        <button
          type="button"
          title={t.memoEditDate}
          aria-label={t.memoEditDate}
          onClick={() => {
            setDraft(memo.meeting_date);
            setEditing(true);
          }}
          style={{ ...iconBtn, alignSelf: "center" }}
        >
          <Icon name="edit" size={15} color="var(--ink-3)" />
        </button>
      </span>
    );
  }

  const commit = () => {
    if (!draft || mut.patchHeader.isPending) return;
    mut.patchHeader.mutate(
      { meeting_date: draft },
      {
        onSuccess: () => {
          setEditing(false);
          showToast(t.memoDateUpdated, "success");
        },
        onError: (err) => {
          // The backend returns a German 409 detail on a same-date clash; surface it verbatim.
          const msg =
            err instanceof ApiError && err.status === 409 ? err.message : t.memoDateUpdateFailed;
          showToast(msg, "error");
        },
      },
    );
  };

  return (
    <span style={{ display: "inline-flex", alignItems: "center", gap: 6 }}>
      <input
        type="date"
        autoFocus
        value={draft}
        onChange={(e) => setDraft(e.target.value)}
        onKeyDown={(e) => {
          if (e.key === "Enter") commit();
          else if (e.key === "Escape") setEditing(false);
        }}
        style={{
          fontSize: 18,
          fontFamily: "var(--font-display)",
          padding: "4px 10px",
          borderRadius: 8,
          border: "1px solid var(--hairline)",
          background: "var(--surface)",
          color: "var(--ink-1)",
        }}
      />
      <button
        type="button"
        title={t.memoDateConfirm}
        disabled={!draft || mut.patchHeader.isPending}
        onClick={commit}
        style={{ ...iconBtn, cursor: draft ? "pointer" : "default" }}
      >
        <Icon name="check" size={16} color="var(--ink-2)" />
      </button>
      <button
        type="button"
        title={t.memoDateCancel}
        onClick={() => setEditing(false)}
        style={iconBtn}
      >
        <Icon name="x" size={15} color="var(--ink-3)" />
      </button>
    </span>
  );
}
